/**
 * Period keys exactly as the server writes them (common/time/Periods.java):
 * daily 2026-08-26, weekly 2026-W35 (ISO, Monday-based), monthly 2026-08.
 * Pure date arithmetic on calendar days, so it never depends on the time of day.
 */
export type RitualKind = 'daily' | 'weekly' | 'monthly';

const pad = (n: number) => String(n).padStart(2, '0');

function utcDay(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m - 1, d));
}

function dayString(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function isoWeekKey(d: Date): string {
  // The ISO week-year is the year of this week's Thursday.
  const thursday = new Date(d);
  thursday.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const year = thursday.getUTCFullYear();
  const jan4 = utcDay(year, 1, 4);
  const week = 1 + Math.round(((thursday.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return `${year}-W${pad(week)}`;
}

function mondayOfIsoWeek(year: number, week: number): Date {
  const jan4 = utcDay(year, 1, 4);
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
  return monday;
}

/** The key `delta` periods away from `key` (negative = into the past). */
export function shiftPeriod(kind: RitualKind, key: string, delta: number): string {
  if (kind === 'daily') {
    const [y, m, d] = key.split('-').map(Number);
    const day = utcDay(y, m, d);
    day.setUTCDate(day.getUTCDate() + delta);
    return dayString(day);
  }
  if (kind === 'monthly') {
    const [y, m] = key.split('-').map(Number);
    const total = y * 12 + (m - 1) + delta;
    return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}`;
  }
  const [y, w] = key.split('-W').map(Number);
  const monday = mondayOfIsoWeek(y, w);
  monday.setUTCDate(monday.getUTCDate() + delta * 7);
  return isoWeekKey(monday);
}

/** Human label: the key itself, plus the date range for a week. */
export function periodLabel(kind: RitualKind, key: string): string {
  if (kind !== 'weekly') return key;
  const [y, w] = key.split('-W').map(Number);
  const monday = mondayOfIsoWeek(y, w);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const short = (d: Date) => `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}`;
  return `${key} (${short(monday)}-${short(sunday)})`;
}
